import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, PackageCheck } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { api, money } from "@/lib/api";

const POLL_MS = 20000;

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const controller = new AbortController();
    let inFlight = false;
    let unauthorized = false;
    const load = async () => {
      if (inFlight || unauthorized || controller.signal.aborted) return;
      inFlight = true;
      try {
        const { data } = await api.get("/admin/notifications", {
          timeout: 20000, signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          setItems(data.items || []);
          setUnreadCount(data.unread_count || 0);
        }
      } catch (error) {
        unauthorized = [401, 403].includes(error.response?.status);
      } finally {
        inFlight = false;
      }
    };
    load();
    const id = setInterval(load, POLL_MS);
    return () => { clearInterval(id); controller.abort(); };
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openNotification = async (n) => {
    setOpen(false);
    if (!n.read) {
      setItems((list) => list.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnreadCount((c) => Math.max(0, c - 1));
      api.put(`/admin/notifications/${n.id}/read`).catch(() => {});
    }
    navigate(`/admin/orders?order=${n.order_id}`);
  };

  const markAllRead = async () => {
    setItems((list) => list.map((x) => ({ ...x, read: true })));
    setUnreadCount(0);
    api.put("/admin/notifications/read-all").catch(() => {});
  };

  return (
    <div className="relative" ref={boxRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-navy/70 hover:bg-cream"
        aria-label="Notifications"
        data-testid="admin-notification-bell"
      >
        <Bell size={19} />
        {unreadCount > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-sunset px-1 text-[10px] font-bold text-white"
            data-testid="admin-notification-count"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 z-50 mt-2 w-[min(360px,90vw)] overflow-hidden rounded-2xl border border-border bg-white shadow-xl"
          data-testid="admin-notification-panel"
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <span className="font-head text-sm font-bold text-navy">Notifications</span>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs font-semibold text-gold-600 hover:underline">
                Mark all as read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">No notifications yet.</p>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => openNotification(n)}
                  className={`flex w-full items-start gap-3 border-b border-border px-4 py-3 text-left text-sm last:border-0 hover:bg-cream ${!n.read ? "bg-gold/5" : ""}`}
                  data-testid={`admin-notification-${n.order_id}`}
                >
                  <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-palm-light text-palm-500">
                    <PackageCheck size={15} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-navy">New paid order</span>
                    <span className="block truncate text-navy/70">
                      {n.order_number} &middot; {money(n.amount)}
                    </span>
                    {n.customer_name && <span className="block truncate text-xs text-muted-foreground">{n.customer_name}</span>}
                    <span className="block text-xs text-muted-foreground">
                      {n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true }) : ""}
                    </span>
                  </span>
                  {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-sunset" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
